/**
 * Selected work. Each row mirrors the reference layout (measured at 1280px wide):
 * - "large": ~49% wide, roughly square (617×550)
 * - "small": ~33% wide, dropped ~60px lower (407×343)
 * - "wide":  ~66% wide, indented ~17% from the left (16:10)
 */
export type ProjectSize = 'large' | 'small' | 'wide';

export interface Project {
  /** Brand name, one or two words */
  client: string;
  /** One line on what was done */
  summary: string;
  /** Service areas, shown as a dot-separated line */
  services: string[];
  image: string;
  alt: string;
  size: ProjectSize;
  /** Wide rows only: which edge the visual hugs (default right) */
  align?: 'left' | 'right';
}

export const projectRows: Project[][] = [
  [
    {
      client: 'Botanica',
      summary: 'Branding a smart lock that feels as safe as it looks',
      services: ['Brand Identity', '3D', 'Product Launch'],
      image: 'https://cdn.magicpatterns.com/uploads/rgZqE5Y4XoRJoJwNssfqtb/33a38b12d780dbf95b1132d4695d5052.jpg',
      alt: 'Translucent green padlock with a fingerprint sensor resting on a white fluted plinth',
      size: 'large',
    },
    {
      client: 'Civica',
      summary: 'A new mark and icon system for a civic data platform',
      services: ['Logo', 'App Icon', 'Design System'],
      image: 'https://cdn.magicpatterns.com/uploads/nqJaqZgLkJFDjBAGw6qbHS/5403297ce139509997248342d162eed4.jpg',
      alt: 'Soft white app icon with a glossy blue and teal letter A',
      size: 'small',
    },
  ],
  [
    {
      client: 'Influx',
      summary: 'Designing the visual language of an AI search experience',
      services: ['AI Product', 'Motion', 'UI/UX', 'Art Direction'],
      image: 'https://cdn.magicpatterns.com/uploads/8WfZu7ucJL8KTChPP9f2Kg/c80a3c016f46dd5db6d069f52934b9e6.jpg',
      alt: 'Hand holding a phone showing a statistics app with a progress score of 78%',
      size: 'wide',
    },
  ],
  [
    {
      client: 'Ōra',
      summary: 'A calm, tactile identity for a wellbeing app',
      services: ['Brand Identity', 'App Icon', 'Typography'],
      image: 'https://cdn.magicpatterns.com/uploads/4wgWU9rvH7kKoSB8mQcUS3/956fae387427ea28d3535d1d04620f09.jpg',
      alt: 'Glossy navy app icon with a flowing letterform on a lavender background',
      size: 'small',
    },
    {
      client: 'Nord Bank',
      summary: 'Reimagining everyday banking for a new generation',
      services: ['Mobile App', 'Fintech', 'UI/UX', 'Branding'],
      image: 'https://cdn.magicpatterns.com/uploads/87SiNnrkf3gkXrp4cgdMFU/d08b31aef9c82de09b9e3b54d0630677.jpg',
      alt: 'Hand holding a phone showing a dark wallet app with balance and transactions',
      size: 'large',
    },
  ],
  [
    {
      client: 'Chron',
      summary: 'A loud, typographic identity for a culture magazine gone digital',
      services: ['Brand Identity', 'Typography', 'Mobile App', 'Campaign'],
      image: 'https://cdn.magicpatterns.com/uploads/6gkzjJPAn7G3EPyN2qnatm/422ce344eb1e9f761b9864874e5d9d5a.jpg',
      alt: 'Man holding an orange phone with a bold blue and grey typographic poster on screen',
      size: 'wide',
      align: 'left',
    },
  ],
  [
    {
      client: 'Loop',
      summary: 'Rebranding a student mobility app with energy to spare',
      services: ['Rebrand', 'Art Direction', 'Campaign'],
      image: 'https://cdn.magicpatterns.com/uploads/qx98b3yLgwLQHrnrHFngUB/981ceddea08c394994eaf1ed57b6915a.jpg',
      alt: 'Smiling student in an orange sweater jumping in front of a dark green looping logo on lime',
      size: 'large',
    },
    {
      client: 'Switchboard',
      summary: 'A tactile 3D language for a smart home control platform',
      services: ['3D', 'Product Design', 'Design System'],
      image: 'https://cdn.magicpatterns.com/uploads/9QDTHq6wXVkXBJLgLpr3ZL/cd771ffba3fa52382c631d68b416601b.jpg',
      alt: 'Soft 3D rendering of two silver and graphite toggle switches on layered rounded plates',
      size: 'small',
    },
  ],
];
